import { isEqual } from "lodash-es";
import { type Ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { useNotify } from "shared/composables/useNotify";
import useTeamPicker from "shared/composables/useTeamPicker";
import { serializeAdvancedFilters } from "shared/helpers/advancedFilters";
import { social, sources } from "shared/helpers/media";
import { type DialogServiceOptions } from "shared/modals/DialogService";
import { normalizeChartSetting } from "shared/modules/Charts/helpers/charts";
import {
  type ChartIndex,
  type SavedChartSetting,
} from "shared/modules/Charts/types";
import { isChartSettingWithStreamsAndMediaTypes } from "shared/modules/Charts/types/guards";
import { useFavorites } from "shared/modules/Insights/composables/actions/useFavorites";
import { useInsightsActions } from "shared/modules/Insights/composables/actions/useInsightsActions";
import { useInsightsGenerateReport } from "shared/modules/Insights/composables/actions/useInsightsGenerateReport";
import { useInsightsShareReport } from "shared/modules/Insights/composables/actions/useInsightsShareReport";
import {
  InsightsAction,
  type InsightsDashboardFormData,
} from "shared/modules/Insights/types";
import { AIVisibilityProject, InstantInsightsReport } from "shared/resources";
import InstantInsightsReportService from "shared/services/api/InstantInsightsReportService";
import { type Nullable } from "shared/types";

export function useInsightsDashboardActions(
  useInsightsActionsInstance?: ReturnType<typeof useInsightsActions>
) {
  const { getInsightsActionHandler, hasOpenConfirmDialog, loadingActions } =
    useInsightsActionsInstance ?? useInsightsActions();

  const { saveReportAsPdf, saveReportAsScheduledReport, saveReportAsXlsx } =
    useInsightsGenerateReport();

  const { shareInsightsReport } = useInsightsShareReport();

  const { toggleFavorite } = useFavorites();

  const { successNotification } = useNotify();

  const { selectedTeamPreference } = useTeamPicker() as unknown as {
    selectedTeamPreference: Ref<number>;
  };

  function hasChartSettingsChanged(
    dashboard: InstantInsightsReport,
    formData: InsightsDashboardFormData
  ): boolean {
    const formDataChartUuids = formData.newChartSettings.map(
      ({ uuid }) => uuid
    );

    const removedCharts = dashboard.newChartSettings.filter(
      ({ uuid }) => !formDataChartUuids.includes(uuid)
    );

    return dashboard.newChartSettings.some((item) => {
      if (removedCharts.includes(item)) {
        return false;
      }

      const updatedItem = formData.newChartSettings.find(
        ({ uuid }) => uuid === item.uuid
      );

      return !isEqual(item, updatedItem);
    });
  }

  function handleSaveDashboard(
    dashboard: InstantInsightsReport,
    formData: InsightsDashboardFormData,
    skipConfirmDialog: boolean = false
  ): Promise<boolean> {
    if (!dashboard) {
      return Promise.resolve(false);
    }

    const isCreating: boolean = !dashboard.id;
    let shouldConfirmSave: boolean = false;
    let confirmDialog: DialogServiceOptions | undefined;

    if (!skipConfirmDialog) {
      if (hasChartSettingsChanged(dashboard, formData)) {
        shouldConfirmSave = !isCreating;
      }

      if (shouldConfirmSave) {
        confirmDialog = {
          confirmLabel: getLocaleText("insights_dashboard.confirm_save_label"),
          message: getLocaleText("insights_dashboard.confirm_save_message"),
          title: getLocaleText("insights_dashboard.confirm_save_title"),
        };
      }
    }

    // use -1 for create action as we don't have an id yet
    const actionId: number = isCreating ? -1 : Number(dashboard.id);

    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.Save,
      confirmDialog,
      errorMessage: getLocaleText("insights_dashboard.save_dashboard_error"),
      ids: [actionId],
      loadingMessage: getLocaleText(
        "insights_dashboard.save_dashboard_loading"
      ),
      successMessage: getLocaleText(
        "insights_dashboard.save_dashboard_success"
      ),
    });

    return actionHandler(async () => {
      const { after, before, range } = formData.dateRange;

      const sanitizedChartSettings = formData.newChartSettings.map(
        normalizeChartSetting
      );

      const types = sanitizedChartSettings.flatMap((chartSetting) => {
        if (isChartSettingWithStreamsAndMediaTypes(chartSetting)) {
          return chartSetting.types ?? [];
        }

        return [];
      });

      const socialTypes = types.filter((type) =>
        social.relatedFields.includes(type)
      );

      const mediums = sources
        .filter(({ relatedFields }) =>
          types.some((type) => relatedFields.includes(type))
        )
        .map(({ medium }) => medium);

      const contentToggles = sources.reduce<Record<string, boolean>>(
        (acc, source) =>
          Object.assign(acc, { [`${source.field}Content`]: false }),
        {}
      );

      mediums.forEach((medium) => {
        contentToggles[`${medium.toLowerCase()}Content`] = true;
      });

      let { title } = formData;

      if (!title && isCreating) {
        title = getLocaleText("insights_dashboard.new_dashboard");
      }

      const aiVisibilityProject: Nullable<AIVisibilityProject> =
        formData.aiVisibilityProjectId
          ? (await AIVisibilityProject.find(formData.aiVisibilityProjectId))
              .data
          : null;

      dashboard!.assignAttributes({
        advancedFilters: serializeAdvancedFilters(formData.advancedFilters),
        after,
        before,
        collapseSyndicated: formData.syndicationEnabled,
        description: formData.description,
        ...(aiVisibilityProject ? { aiVisibilityProject } : {}),
        name: title,
        newChartSettings: sanitizedChartSettings.map((chart) => ({
          ...chart,
          advanced_filters: serializeAdvancedFilters(chart.advanced_filters),
        })),
        organisationTeamId: selectedTeamPreference.value || null,
        rangeLabel: range?.label || null,
        selectedSocialContent: socialTypes,
        streamIds: formData.streamIds,
        ...contentToggles,
      });

      await dashboard.saveResource({
        returnScope: InstantInsightsReport.withDashboardScope(),
        with: ["aiVisibilityProject"],
      });
    });
  }

  function handleGenerateDashboardPdf(
    dashboard: InstantInsightsReport,
    availableCharts?: ChartIndex
  ): Promise<boolean> {
    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.GeneratePDF,
      errorMessage: getLocaleText("insights_dashboard.generate_pdf_error"),
      ids: [Number(dashboard.id)],
    });

    return actionHandler(() => saveReportAsPdf(dashboard, availableCharts));
  }

  function handleGenerateDashboardXlsx(
    dashboard: InstantInsightsReport,
    availableCharts?: ChartIndex
  ): Promise<boolean> {
    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.GenerateXlsx,
      errorMessage: getLocaleText("insights_dashboard.generate_xlsx_error"),
      ids: [Number(dashboard.id)],
    });

    return actionHandler(() => saveReportAsXlsx(dashboard, availableCharts));
  }

  function handleGenerateDashboardScheduledReport(
    dashboard: InstantInsightsReport
  ): Promise<boolean> {
    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.GenerateScheduledReport,
      errorMessage: getLocaleText(
        "insights_dashboard.generate_scheduled_report_error"
      ),
      ids: [Number(dashboard.id)],
    });

    return actionHandler(() => saveReportAsScheduledReport(dashboard));
  }

  function handleShareDashboard(
    dashboard: InstantInsightsReport
  ): Promise<boolean> {
    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.Share,
      errorMessage: getLocaleText("insights_dashboard.share_error"),
      ids: [Number(dashboard.id)],
    });

    return actionHandler(async () => {
      const message = await shareInsightsReport(dashboard);

      if (message) {
        successNotification({ message });
      }
    });
  }

  function handleToggleFavoriteDashboard(
    dashboard: InstantInsightsReport
  ): Promise<boolean> {
    let hasFavorite = Boolean(dashboard.favorite);

    const loadingMessage = hasFavorite
      ? getLocaleText("insights_dashboard.unstar_loading")
      : getLocaleText("insights_dashboard.star_loading");

    const errorMessage = hasFavorite
      ? getLocaleText("insights_dashboard.unstar_error")
      : getLocaleText("insights_dashboard.star_error");

    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.Star,
      errorMessage,
      ids: [Number(dashboard.id)],
      loadingMessage,
    });

    return actionHandler(async () => {
      const updatedItem = await toggleFavorite(dashboard);

      hasFavorite = Boolean(updatedItem.favorite);

      const successMessage = hasFavorite
        ? getLocaleText("insights_dashboard.star_success")
        : getLocaleText("insights_dashboard.unstar_success");

      successNotification({ message: successMessage });
    });
  }

  function handleDuplicateDashboard(
    dashboard: InstantInsightsReport
  ): Promise<boolean> {
    const updatedIds = [Number(dashboard.id)];

    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.Duplicate,
      errorMessage: getLocaleText("insights_landing_page.duplicate_error"),
      ids: updatedIds,
      loadingMessage: getLocaleText("insights_dashboard.duplicate_loading"),
      successMessage: getLocaleText("insights_dashboard.duplicate_success"),
    });

    return actionHandler(async () => {
      await InstantInsightsReportService.duplicate(
        updatedIds[0],
        selectedTeamPreference.value || null
      );
    });
  }

  function handleDeleteChart(
    dashboard: InstantInsightsReport,
    chart: SavedChartSetting
  ): Promise<boolean> {
    const updatedIds = [chart.uuid];

    const actionHandler = getInsightsActionHandler({
      action: InsightsAction.DeleteChart,
      confirmDialog: {
        cancelLabel: getLocaleText("global.cancel"),
        confirmLabel: getLocaleText("global.confirm"),
        message: getLocaleText("insights_dashboard.delete_chart_message"),
        title: getLocaleText("insights_dashboard.delete_chart", {
          chart: chart.label,
        }),
      },
      errorMessage: getLocaleText("insights_dashboard.delete_chart_error"),
      ids: updatedIds,
      loadingMessage: getLocaleText("insights_dashboard.delete_chart_loading"),
      successMessage: getLocaleText("insights_dashboard.delete_chart_success"),
    });

    return actionHandler(async () => {
      const updatedSettings = dashboard.newChartSettings.filter(
        ({ uuid }) => chart.uuid !== uuid
      );

      dashboard.assignAttributes({ newChartSettings: updatedSettings });

      await dashboard.saveResource({
        returnScope: InstantInsightsReport.withDashboardScope(),
      });
    });
  }

  return {
    handleDeleteChart,
    handleDuplicateDashboard,
    handleGenerateDashboardPdf,
    handleGenerateDashboardScheduledReport,
    handleGenerateDashboardXlsx,
    handleSaveDashboard,
    handleShareDashboard,
    handleToggleFavoriteDashboard,
    hasOpenConfirmDialog,
    loadingActions,
  };
}

export default useInsightsDashboardActions;
